import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityData,
  ActivityDataInput,
  NewActivity
} from "@/entities/activities/types";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import FormNew from "@/components/controls/Form/FormNew";
import { FormSectionProps } from "@/components/controls/Form/FormSection";
import Modal from "@/components/controls/Modal";
import Text from "@/components/ui/text";
import { getEnumTKey } from "@/utils/strings";
import { EntityType } from "@/services/hooks/entities/useEntityType";
import FileUpload from "@/components/controls/FileUpload/FileUpload";
import { SavedAttachmentInfo } from "@/entities/attachments/types";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "@/components/ui/tooltip";
import { EntityData } from "@/services/api/types";
import { useActivityModal } from "@/components/layouts/Activities/hooks/useActivityModal";
import { mergeActivitySubmissionNotes } from "@/components/layouts/Activities/hooks/useActivityModalSubmission";
import { isUndefined } from "lodash";
import CreatePersonModal from "@/components/layouts/CreatePersonModal";
import { getMustSelectCompanyText } from "@/entities/activities/helpers";

interface Props {
  isOpen: boolean;
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
  entityType: EntityType;
  entityData: EntityData | undefined;
  pageName: string;
  isEditMode: boolean;
  editActivityData?: ActivityData | null;
  resetPage?: () => void;
}

const QuoteActivityModal: React.FC<Props> = ({
  isOpen,
  setIsOpen,
  entityType,
  entityData,
  pageName,
  isEditMode,
  editActivityData,
  resetPage
}) => {
  const { t } = useTranslation();

  const {
    formRef,
    isFormValid,
    setIsFormValid,
    handleSetValue,
    isCompanySet,
    activityData,
    handleSubmit: baseHandleSubmit,
    getBaseFields,
    getParticipantsField,
    processFieldsForEditMode,
    isCreatePersonModalOpen,
    setIsCreatePersonModalOpen,
    onPersonCreation,
    effectiveCompanyId
  } = useActivityModal({
    activityType: NewActivity.QUOTE,
    isOpen,
    setIsOpen,
    entityType,
    entityData,
    pageName,
    isEditMode,
    editActivityData,
    resetPage
  });

  const [fileAttachments, setFileAttachments] = useState<SavedAttachmentInfo[]>(
    []
  );
  const [attachmentsChanged, setAttachmentsChanged] = useState(false);
  const initializedActivityRef = useRef<string | null>(null);
  const [isUploading, setIsUploading] = useState(false);

  const newActivityKey = getEnumTKey(
    "activities",
    "newActivities",
    NewActivity,
    NewActivity.QUOTE
  );

  const formSections = useMemo<FormSectionProps[]>(() => {
    const baseFields = getBaseFields();
    const quoteFieldNames = ["date", "time", "participants"];
    let fields = quoteFieldNames
      .map((name) => {
        if (name === "participants") {
          return getParticipantsField(t("activities.activityModal.sentTo"));
        }
        return baseFields[name as keyof typeof baseFields];
      })
      .filter(Boolean);

    fields = processFieldsForEditMode(fields);

    return [
      {
        id: "quote-form-section",
        fields
      }
    ];
  }, [getBaseFields, getParticipantsField, processFieldsForEditMode, t]);

  useEffect(() => {
    if (!isOpen) {
      initializedActivityRef.current = null;
      return;
    }
    const activityKey = isEditMode ? activityData?.id : "new-quote";
    if (!activityKey || initializedActivityRef.current === activityKey) return;

    if (isEditMode && !isUndefined(activityData)) {
      // Set content for special fields
      setFileAttachments(activityData.notes?.attachments ?? []);
      setAttachmentsChanged(false);
      initializedActivityRef.current = activityKey;
      return;
    }
    if (!isEditMode) {
      initializedActivityRef.current = activityKey;
    }
  }, [activityData, isEditMode, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setFileAttachments([]);
      setAttachmentsChanged(false);
      setIsUploading(false);
    }
  }, [isOpen]);

  const handleFileAttachments = (attachments: SavedAttachmentInfo[]) => {
    setFileAttachments(attachments);
    setAttachmentsChanged(true);
  };

  const handleUploadingChange = (uploading: boolean) => {
    setIsUploading(uploading);
  };

  const handleSubmit = async (data: ActivityDataInput) => {
    await baseHandleSubmit(data, (baseData) =>
      mergeActivitySubmissionNotes(baseData, {
        ...(!isEditMode ? { meetingContent: "" } : {}),
        attachments: attachmentsChanged ? fileAttachments : undefined
      })
    );
  };

  const handleCancel = () => {
    setIsOpen(false);
  };

  const handleCreateNew = () => {
    formRef.current?.requestSubmit();
  };

  return (
    <>
      <Modal
        open={isOpen}
        setIsOpen={setIsOpen}
        contentClassname="w-[35rem]"
        title={
          <Text as="div" variant="lgSubtitleH1">
            {t(`activities.activityModal.${isEditMode ? "edit" : "add"}`, {
              type: t(newActivityKey).toLowerCase(),
              activity: ""
            })}
          </Text>
        }
        content={
          <div
            className={`flex flex-col gap-3 w-[35rem] p-6 h-fit overflow-y-auto max-h-[75vh]`}
          >
            {/* Hidden element to capture initial focus */}
            <div tabIndex={0} className="sr-only" />
            <FormNew
              sections={formSections}
              isFormValid={(isValid) => setIsFormValid(isValid)}
              onSubmit={handleSubmit}
              formRef={formRef}
              onSetValue={handleSetValue}
            />
            <div className="flex flex-col gap-2">
              <Text variant="bodySecondary">{`${t(newActivityKey)} files`}</Text>
              <FileUpload
                key={activityData?.id ?? "new-quote"}
                onAttachmentsChange={handleFileAttachments}
                onUploadingChange={handleUploadingChange}
                initialAttachments={
                  isEditMode ? activityData?.notes?.attachments : []
                }
              />
            </div>
          </div>
        }
        footer={
          <div className="flex justify-end gap-2">
            <Button
              data-test="cancel-button"
              variant="secondary"
              onClick={handleCancel}
            >
              {t("cancel")}
            </Button>
            <Tooltip>
              <TooltipTrigger>
                <Button
                  data-test="submit-button"
                  disabled={!isFormValid || isUploading || !isCompanySet}
                  onClick={handleCreateNew}
                >
                  {t(isEditMode ? "save" : "activities.activityModal.add", {
                    type: t(newActivityKey).toLowerCase(),
                    activity: ""
                  })}
                </Button>
              </TooltipTrigger>
              {!isCompanySet && (
                <TooltipContent maxWidth={15}>
                  {getMustSelectCompanyText(t, entityType)}
                </TooltipContent>
              )}
            </Tooltip>
          </div>
        }
      />
      <CreatePersonModal
        isOpen={isCreatePersonModalOpen}
        setIsOpen={setIsCreatePersonModalOpen}
        onSuccess={onPersonCreation}
        companyId={effectiveCompanyId}
      />
    </>
  );
};

export default QuoteActivityModal;
